import React, { useEffect, useRef, useState } from 'react';
import Modal from '../Modal/Modal';
import { Size } from '../../Enums/Size.enum';
import LinkButton from '../Button/LinkButton';
import { Theme } from '../../Enums/Theme.enum';
import Button from '../Button/Button';
import { useAppContext } from '../../Services/AppContext';
import {
	acceptCase,
	connectWithPatient,
	showCaseDetail,
} from '../../Modules/Case/Services/case.service';
import FullPageLoading from '../Utils/FullPageLoading/FullPageLoading';
import PatientTreatment from '../../Modules/Patient/Components/PatientTreatment';
import {
	useLazyCasesAssignedQuery,
	useLazyPatientProfileDataQuery,
	useWorkplaceMemberQuery,
} from '../../graphql/graphql';
import DeletePatientModal from '../DeletePatient/DeletePatientModal';
import AssignPatientToWorkplaceModal from '../../Modules/Patient/Components/AssignPatientToWorkplace/AssignPatientToWorkplaceModal';
import ConfirmCreateCaseModal from '../Case/ConfirmCreateCaseModal';
import { useEffectAsync } from '../../helpers';
import TreatmentCloseButton from './TreatmentCloseButton';
import { WorkplacePatientStatus } from '../../Modules/EArchive/Enums/WorkplacePatientStatus.enum';
import ConfirmationModal from '../../Modules/Base/Components/Confirmation/ConfirmationModal';
import apiClient from '../../Services/ApiClient';
import useApiClient from '../../Modules/Base/Hooks/useApiClient';
import useNotification from '../../Hooks/useNotification.hook';
import useTranslation from '../../Hooks/useTranslation';
import usePatient from '../../Modules/Patient/Hooks/usePatient';
import ArchivePatientModal from '../../Modules/Patient/Components/ArchivePatient/ArchivePatientModal';

interface TreatmentModalProps {
	opened: boolean;
	onClose?: () => void;
	patientId: string;
	workplaceId?: string;
	loggedUserId?: string;
	caseId?: string;
	onRefresh?: () => void;
}

const TreatmentModal: React.FC<TreatmentModalProps> = ({
	opened,
	onClose,
	patientId,
	workplaceId,
	caseId,
	onRefresh,
}) => {
	const { t } = useTranslation();
	const actualWorkplaceId = useRef(workplaceId);
	const appContext = useAppContext();
	const [loading, setLoading] = useState(false);
	const [isPatientDeleteModalOpen, setIsPatientDeleteModalOpen] =
		useState(false);
	const [isPatientAssignToWorkplaceOpen, setIsPatientAssignToWorkplaceOpen] =
		useState(false);
	const [isArchived, setIsArchived] = useState(false);
	const [isUnArchiveConfirmationOpen, setIsUnArchiveConfirmationOpen] =
		useState(false);
	const [isArchivePatientOpen, setIsArchivePatientOpen] = useState(false);

	const [isConfirmCreateCaseOpen, setIsConfirmCreateCaseOpen] = useState(false);
	const { data: workplaceMember } = useWorkplaceMemberQuery({
		userId: patientId,
		workplaceId: appContext.workplaceId,
	});
	const [getCases, { data: casesWithPatient }] = useLazyCasesAssignedQuery();
	const [getPatientProfile, { data: patientProfile }] =
		useLazyPatientProfileDataQuery();

	const { unArchivePatient } = usePatient();

	useEffect(() => {
		const status = workplaceMember?.workplaceMember?.status;
		if (status) {
			const archivedStatuses = [
				WorkplacePatientStatus.ARCHIVED,
				WorkplacePatientStatus.ARCHIVED_BY_PATIENT,
				WorkplacePatientStatus.ARCHIVED_BY_WORKPLACE,
			];
			if (archivedStatuses.includes(status as WorkplacePatientStatus)) {
				setIsArchived(true);
			}
		}
	}, [workplaceMember]);

	useEffect(() => {
		actualWorkplaceId.current =
			actualWorkplaceId.current ?? appContext.workplaceId;
	}, [appContext]);

	useEffectAsync(async () => {
		if (opened && patientId) {
			await getCases({
				patientId: patientId,
				doctorId: appContext?.userData?.id,
				workplaceId: workplaceId ?? appContext.workplaceId,
				onlyVisible: true,
				take: 999,
			});
			await getPatientProfile({
				userId: patientId,
			});
		}
	}, [opened]);

	const handlePressConnectWithPatient = async () => {
		if (casesWithPatient?.casesAssigned?.data?.length) {
			setIsConfirmCreateCaseOpen(true);
		} else {
			handleConnectWithPatient();
		}
	};
	const handleConnectWithPatient = async () => {
		setLoading(true);
		const loggedUserId = appContext?.userData?.id;
		const localWorkplaceId = appContext.workplaceId;

		try {
			if (caseId) {
				await acceptCase(caseId, loggedUserId);
			} else {
				await connectWithPatient(loggedUserId, patientId, localWorkplaceId);
			}
		} catch (e) {
			setLoading(false);
		}
	};

	const onPatientDelete = async () => {
		onClose?.();
		// @ts-ignore: Protože není exposed window objekt -> asi bude chtít upravit tsconfig
		window.location.reload();
	};

	const handleCloseUnArchiveConfirmation = () => {
		setIsUnArchiveConfirmationOpen(false);
	};

	const handleUnArchivePatient = async () => {
		const result = await unArchivePatient({
			workplaceId: appContext.workplaceId,
			patientId,
		});
		if (result) {
			onRefresh?.();
		}
		handleCloseUnArchiveConfirmation();
	};

	if (!opened) {
		return <></>;
	}

	return (
		<>
			{loading && <FullPageLoading />}
			<Modal opened={opened} onClose={onClose} size={Size.FULLSCREEN}>
				<Modal.Content contentStyle={{ padding: '0 12px 0 0', height: '100%' }}>
					<div className='patient-profile__panel'>
						<div className='patient-profile__panel__content'>
							<PatientTreatment patientId={patientId} />
						</div>
						<div className='d-flex flex-column'>
							<div className='patient-profile__panel__controls'>
								<TreatmentCloseButton onClose={onClose} />
							</div>
							<div className='d-flex flex-column patient-profile__panel__menu'>
								<Button
									onClick={handlePressConnectWithPatient}
									title={'base.shared.contactAPatient'}
									theme={Theme.GREEN}
								/>
								<div className='pt-3 mt-auto text-center'>
									{isArchived ? (
										<>
											<LinkButton
												onClick={() => setIsUnArchiveConfirmationOpen(true)}
												title='component.patient.unArchive'
												theme={Theme.BLUE}
											/>
											{isUnArchiveConfirmationOpen && (
												<ConfirmationModal
													isOpen
													title='component.patient.unArchiveConfirmationTitle'
													text={
														t('component.patient.unArchiveConfirmationText') ??
														''
													}
													onClose={handleCloseUnArchiveConfirmation}>
													<div
														className='d-flex flex-row align-items-center'
														style={{ gap: '15px' }}>
														<Button
															onClick={handleUnArchivePatient}
															title='base.yes'
															theme={Theme.GREEN}
														/>
														<LinkButton
															onClick={handleCloseUnArchiveConfirmation}
															title='base.shared.cancel'
															theme={Theme.RED}
															firstUpper
														/>
													</div>
												</ConfirmationModal>
											)}
										</>
									) : (
										<>
											<div className='mb-3'>
												<LinkButton
													onClick={() =>
														setIsPatientAssignToWorkplaceOpen(true)
													}
													title='component.assignPatientToWorkplace.triggerButton'
													theme={Theme.BLUE}
												/>
											</div>
											<div className='mb-3'>
												<LinkButton
													onClick={() => setIsArchivePatientOpen(true)}
													title='component.archivePatient.triggerButton'
													theme={Theme.BLUE}
												/>
											</div>
											<LinkButton
												onClick={() => {
													setIsPatientDeleteModalOpen(true);
												}}
												title='component.patient.removeFromEArchive'
												theme={Theme.RED}
											/>
										</>
									)}
								</div>
							</div>
						</div>
					</div>
				</Modal.Content>
			</Modal>
			<DeletePatientModal
				opened={isPatientDeleteModalOpen}
				onClose={() => setIsPatientDeleteModalOpen(false)}
				onPatientDelete={onPatientDelete}
				patientId={patientId}
			/>
			<AssignPatientToWorkplaceModal
				isOpen={isPatientAssignToWorkplaceOpen}
				setIsOpen={setIsPatientAssignToWorkplaceOpen}
				patientId={patientId}
			/>
			{isArchivePatientOpen && (
				<ArchivePatientModal
					patientId={patientId}
					workplaceId={workplaceId || appContext.workplaceId}
					isOpen
					onClose={() => setIsArchivePatientOpen(false)}
					onSuccess={onPatientDelete}
				/>
			)}
			<ConfirmCreateCaseModal
				isOpen={isConfirmCreateCaseOpen}
				patientName={patientProfile?.user?.profile?.fullName ?? ''}
				onCaseClick={caseId => {
					showCaseDetail(caseId);
				}}
				patientId={patientId}
				cases={
					casesWithPatient?.casesAssigned?.data
						?.map(item => {
							return {
								id: item?.id,
								name: item?.title ?? '',
							};
						})
						.filter((item): item is { id: string; name: string } => !!item) ??
					[]
				}
				onClose={() => setIsConfirmCreateCaseOpen(false)}
			/>
		</>
	);
};

export default TreatmentModal;
